<script>
/**
 * WebhookQuicktable
 * 
 * @author Daniel Lepe
 * @date 16 de Oct, 2024
 * @version 1.0
 */
import _ from 'lodash'
import axios from 'axios'
import automodal from './WebhookAutomodal.vue'
import QuickCardFooter from '@/views/elements/quick-card-footer.vue'
import navigatorWebhookAndRequestcache from '../Requestcache/navigatorWebhookAndRequestcache.vue'

import { captions, quicktableFilters, baseIndex as fetchingAPI } from './archetype.js'
import { notify } from '../../utils.js'

let name = 'WebhookQuicktable',
	newButtonLabel = 'New',
	emptyHeader = 'Data not found',
	emptyLead = 'Module empty',
	reloadData = 'Try reloading this page',
	tryAgainLater = '',
	header = null,
	footer = null;

export default {
	name,

	components: {
		automodal,
		QuickCardFooter,
		navigatorWebhookAndRequestcache
	},

	props: {
		value: {
			type: [Object, Number, String]
		},
		valueName: {
			type: String,
			default: 'id'
		},
		disableNew: {
			type: Boolean,
			default: true
		},
		selectMode: {
			type: Boolean,
			default: false
		},
		hideNavigation: {
			type: Boolean,
			default: false
		},
		fetchingAPI: {
			type: String,
			default: () => { return fetchingAPI }
		},
		header: {
			type: String,
			default: () => { return header }
		},
		perPage: {
			type: Number,
			default: 10,
		},
		footer: {
			type: String,
			default: () => { return footer }
		},
		captions: {
			type: [Array, Object],
			default: () => captions
		},
		args: {
			type: Object,
			default: () => { return {} }
		}
	},

	computed: {
		cCaptions() {
			let r = [];
			try {
				if (this.selectMode) {
					r.push({
						key: 'check',
						label: ''
					})
				}
				_.forEach(this.captions, item => {
					r.push(item)
				})
			} catch (e) {
				console.error(e)
			}
			return r;
		},
		sourceURI() {
			let init = this.fetchingAPI + "/" + this.perPage + "/" + this.currentPage;
			if (this.term) {
				init += "/" + this.term;
			} else if (!this.term || this.term == "s") {
				init += "/@";
			}
			const fullArgs = _.merge(this.args, this.filters);
			init += '/' + this.objectToUrlString(fullArgs)
			return (init.trim()).replace(/[ ]/g, '_');
		}
	},

	data: () => {
		return {
			processingIds: [],
			name, newButtonLabel, emptyHeader,
			emptyLead, tryAgainLater, reloadData,
			currentPage: 1,
			items: [],
			itemsCount: 0,
			term: null, filters: {},
			isLoading: false
		}
	},

	methods: {
		async processWebhook(webhookID) {
			this.processingIds.push(webhookID)
			try {
				const response = await axios.get('webhook/processor/' + webhookID)
				console.log(response)
				if(!_.isObjectLike(response))	
					throw 'Error en la respuesta';
				if(response.status)
					setTimeout(this.loadUp, 15000)
			} catch(e){
				notify(e)
			}
			console.log('this.processingIds.indexOf(webhookID)', this.processingIds.indexOf(webhookID))
			this.processingIds.splice(this.processingIds.indexOf(webhookID), 1)
		},
		clickWebhook(webhookID){
			this.processWebhook(webhookID)
		},
		isProcessingID(webhookID) {
			return (this.processingIds.indexOf(webhookID) !== -1)
		},
		isSelected(data){
			// console.log('isSelected', data, this.value, (this.value == data.item[this.valueName]))
			return (this.value == data.item[this.valueName])
		},
		rowClicked(item, index, event) {
			this.$emit('setItem', item, event)
			this.$nextTick(() => {
				if (this.selectMode) {
					this.$emit('input', item[this.valueName], { item, index, event })
				}
			})
		},
		quicktableFilters,
		objectToUrlString(obj) {
			return Object.entries(obj)
				.map(([key, value]) => {
					const valueFixed = value.replace(/[ ]/g, '_');
					return `${encodeURIComponent(key)}=${encodeURIComponent(valueFixed)}`;
				})
				.join(':');
		},
		async refreshFromStart() {
			this.currentPage = 1
			this.refresh()
		},
		async refresh() {
			this.isLoading = true
			await this.loadUp()
			this.isLoading = false
		},
		async loadUp() {
			try {
				let response = await axios.get(this.sourceURI)
				this.items = response.data.rows;
				this.itemsCount = response.data.count;
			} catch (error) {
				this.items = [];
				this.itemsCount = 0;
				console.error(error)
			}
		}
	},

	watch: {
		currentPage(a, b) {
			this.refresh();
		},
		'$route'() {
			this.loadUp()
		}
	},

	mounted() {
		this.refresh()
	}
};
</script>

<template>
	<div :id="name">

		<navigator-webhook-and-requestcache />

		<b-card no-body>

			<!-- TABLE CONTENT -->
			<b-table  responsive class="animated fadeIn" v-if="items.length && !isLoading" 
				@row-clicked="rowClicked" 
				:hover="selectMode" 
				:items="items" 
				:fields="cCaptions">

				<template #cell(check)="data">
					<div>
						<input type="checkbox" :checked="isSelected(data)" @click.prevent="()=> {}"/>
					</div>
				</template>

				<template #cell(costo)="data">
					{{ data.value | currency }}
				</template>

				<template #cell(imagenes_id)="data">
					<api-img-pristine :comp="data.value" size="128/128" :thumbnail="true" :allow-zoom="true" />
				</template>

				<template #cell(suscripcion)="data">
					<suscription-automodal @refresh="refresh"  
						v-if="data.item.usuarios_suscripciones_id" 
						:concept-id="data.item.usuarios_suscripciones_id" 
						:args="args" 
						:key="'sus-automod-' + data.item.id">
						{{data.value}}
					</suscription-automodal>
				</template>

				<template #cell(_)="data">
					<b-button-group>
						<b-button variant="success" v-if="data.item.estatus == 'Nuevo'" @click="clickWebhook(data.item.id)" >
							<span v-if="isProcessingID(data.item.id)" key="spin"><i class="fa fa-cog fa-spin"></i></span>
							<span v-else key="bolt"><i class="fa fa-bolt"></i></span>
						</b-button>
						<automodal :concept-id="data.item.id" @refresh="refresh"  :args="args">
							Ver registro
						</automodal>
					</b-button-group>
				</template>

			</b-table>

			<!-- LOADING SPINNER -->

			<div v-if="isLoading">
				<div class="text-center p-5">
					<b-spinner variant="primary" />
				</div>
			</div>

			<!-- EMPTY JUMBOTRON -->

			<div v-else-if="!items.length" class="pt-4 pl-4 pr-4 pb-0">
				<b-jumbotron :header="emptyHeader" :lead="emptyLead">
					<p>{{ tryAgainLater }}</p>
					<b-button variant="primary" @click="refresh()">{{ reloadData }}</b-button>
				</b-jumbotron>
			</div>

			<!-- HEADER -->

			<template v-slot:header>
				<b-row align-h="between">

					<b-col md="auto" v-if="!!header">
						<h6 class="mt-2 mb-2">{{ header }}</h6>
					</b-col>

					<b-col>
						<b-input-group>
							<b-form-input v-model="term" @keyup.enter="refreshFromStart()" placeholder="Search" />
							<b-input-group-append>
								<b-button v-if="!!term" variant="secondary" @click="term = ''; refreshFromStart();"><i
										class="fa fa-times"></i></b-button>
								<b-button variant="secondary" @click="refreshFromStart()" :disabled="!term"><i
										class="fa fa-search"></i></b-button>
							</b-input-group-append>
						</b-input-group>
					</b-col>

					<quicktable-filters v-model="filters" @changed="refreshFromStart" :filters="quicktableFilters" />

					<b-col md="auto" class="align-right">
						<automodal @refresh="refresh" :args="args" v-if="!disableNew">
							Nuevo
						</automodal>
					</b-col>

				</b-row>
			</template>

			<!-- FOOTER -->

			<template v-slot:footer v-if="!isLoading && items.length">
				<quick-card-footer :items-count="itemsCount" :per-page="perPage" v-model="currentPage"
					@refresh="refresh" :items-fetched="items.length" />
			</template>

		</b-card>

	</div>
</template>